import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ChatTeardropText, X, HandHeart, CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = "cf_painpoint_seen";

const STAGES = [
    { key: "before", label: "Before" },
    { key: "during", label: "During" },
    { key: "after", label: "After" },
    { key: "all", label: "All of it" },
];

/**
 * Public "Pain Point" capture — an exit-intent popup (desktop) / ~30s delay
 * (mobile), shown once per visitor, plus an always-available corner tab so
 * anyone can open it whenever they like. Thanks the visitor, acknowledges that
 * switching platforms is hard, and asks where their gala pain points are.
 */
export default function PainPointWidget() {
    const [open, setOpen] = useState(false);
    const [autoEligible, setAutoEligible] = useState(() => {
        try { return !localStorage.getItem(SEEN_KEY); } catch { return false; }
    });
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [form, setForm] = useState({ name: "", email: "", organization: "", stage: "all", frustration: "", desired_change: "" });

    const markSeen = useCallback(() => {
        try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* noop */ }
        setAutoEligible(false);
    }, []);

    // Auto-trigger: exit-intent on desktop, gentle delay on mobile/touch.
    useEffect(() => {
        if (!autoEligible) return;
        const isTouch = window.matchMedia("(pointer: coarse)").matches;
        let timer;
        const trigger = () => { setOpen(true); markSeen(); cleanup(); };
        const onLeave = (e) => { if (e.clientY <= 0) trigger(); };
        const cleanup = () => {
            document.removeEventListener("mouseout", onLeave);
            if (timer) clearTimeout(timer);
        };
        if (isTouch) {
            timer = setTimeout(trigger, 30000);
        } else {
            document.addEventListener("mouseout", onLeave);
            timer = setTimeout(trigger, 75000); // desktop safety net if they never exit
        }
        return cleanup;
    }, [autoEligible, markSeen]);

    const openManually = () => { setOpen(true); markSeen(); };

    const submit = async (e) => {
        e?.preventDefault();
        if (!/\S+@\S+\.\S+/.test(form.email)) { setErr("Please enter a valid email so we can follow up."); return; }
        if (!form.frustration.trim()) { setErr("Tell us your biggest frustration — even one line helps."); return; }
        setBusy(true); setErr("");
        try {
            await axios.post(`${API}/feedback`, { ...form, page_url: window.location.href });
            setDone(true);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Something went wrong — please try again.");
        } finally { setBusy(false); }
    };

    return (
        <>
            {/* Always-available corner tab */}
            {!open && (
                <button
                    onClick={openManually}
                    className="fixed left-0 top-1/2 -translate-y-1/2 z-[60] bg-accent text-accent-foreground shadow-lg flex items-center justify-center w-8 sm:w-9 py-4 hover:w-10 transition-all"
                    data-testid="painpoint-tab"
                    aria-label="Share a pain point"
                >
                    <span className="flex items-center gap-2" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                        <ChatTeardropText size={18} weight="fill" />
                        <span className="hidden sm:inline text-[11px] font-bold uppercase tracking-[0.2em]">Share a pain point</span>
                    </span>
                </button>
            )}

            {open && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm" data-testid="painpoint-modal" onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
                    <div className="bg-white w-full max-w-lg max-h-[92vh] overflow-y-auto border-t-4 border-accent relative">
                        <button onClick={() => setOpen(false)} className="absolute top-3 right-3 text-foreground/50 hover:text-foreground z-10" data-testid="painpoint-close"><X size={22} /></button>

                        {done ? (
                            <div className="p-10 text-center" data-testid="painpoint-done">
                                <CheckCircle size={64} weight="fill" className="text-accent mx-auto" />
                                <h2 className="font-heading text-3xl text-primary mt-4">Thank you — truly.</h2>
                                <p className="text-foreground/75 mt-3 leading-relaxed">
                                    We read every single one of these. Your pain points shape exactly what we build next — and if you'd like,
                                    we'll personally reach out to show you how we'd solve them for your next gala. We have a <strong>lot</strong> of
                                    flexibility to customize, and we're here to help.
                                </p>
                                <button onClick={() => setOpen(false)} className="mt-6 bg-primary text-primary-foreground px-6 py-3 font-bold uppercase tracking-wider text-sm" data-testid="painpoint-done-close">Close</button>
                                <a href="/problems-we-solve" className="block mt-3 text-sm font-semibold text-accent hover:underline" data-testid="painpoint-done-solve">See how we'd solve these for you →</a>
                            </div>
                        ) : (
                            <div className="p-7 sm:p-8">
                                <div className="flex items-center gap-2 text-accent mb-2">
                                    <HandHeart size={22} weight="fill" />
                                    <span className="label-eyebrow text-[10px]">Before you go…</span>
                                </div>
                                <h2 className="font-heading text-2xl sm:text-3xl text-primary leading-tight">Switching platforms is hard. We get it.</h2>
                                <p className="text-foreground/75 text-sm mt-3 leading-relaxed">
                                    "We've been doing it this way for years" is the most natural thing in the world. So here's our honest ask:
                                    <strong> why use our platform?</strong> Tell us the pain points and problems you hit with your current event
                                    process — before, during, and after your big Gala — and <strong>let's see if we can solve them for you.</strong> We
                                    have real flexibility to customize, and we'd love to earn your trust.
                                </p>

                                <form onSubmit={submit} className="mt-5 space-y-3">
                                    <div className="grid grid-cols-2 gap-2">
                                        <input className="ed-input" placeholder="Your name (optional)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="pp-name" />
                                        <input className="ed-input" placeholder="Organization (optional)" value={form.organization} onChange={e => setForm({ ...form, organization: e.target.value })} data-testid="pp-org" />
                                    </div>
                                    <input className="ed-input w-full" type="email" required placeholder="Email (so we can follow up) *" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="pp-email" />

                                    <div>
                                        <p className="text-xs font-semibold text-foreground/80 mb-1.5">Where do you feel the most pain?</p>
                                        <div className="grid grid-cols-4 gap-1.5">
                                            {STAGES.map(s => (
                                                <button type="button" key={s.key} onClick={() => setForm({ ...form, stage: s.key })}
                                                    className={`py-2 text-xs font-bold uppercase tracking-wide border transition-colors ${form.stage === s.key ? "bg-accent text-accent-foreground border-accent" : "border-border text-foreground/70 hover:border-accent"}`}
                                                    data-testid={`pp-stage-${s.key}`}>{s.label}</button>
                                            ))}
                                        </div>
                                    </div>

                                    <textarea className="ed-input w-full min-h-[80px]" required placeholder="Your current pain points or problems with your event process… *" value={form.frustration} onChange={e => setForm({ ...form, frustration: e.target.value })} data-testid="pp-frustration" />
                                    <textarea className="ed-input w-full min-h-[60px]" placeholder="What would you love us to solve for you? (optional)" value={form.desired_change} onChange={e => setForm({ ...form, desired_change: e.target.value })} data-testid="pp-change" />

                                    {err && <p className="text-destructive text-sm" data-testid="pp-error">{err}</p>}

                                    <button type="submit" disabled={busy} className="w-full bg-accent text-accent-foreground py-3.5 font-bold uppercase tracking-[0.15em] text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="pp-submit">
                                        {busy ? "Sending…" : <><PaperPlaneTilt size={16} weight="fill" /> Send it our way</>}
                                    </button>
                                    <p className="text-[10px] text-foreground/50 text-center">Takes 20 seconds. No sales spam — we just want to help.</p>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
